import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Badge,
  Dropdown,
  Form,
  Toast,
  ToastContainer,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  Package,
  RefreshCw,
  ExternalLink,
  Check,
  GripVertical,
  Edit2,
  Trash2,
  ArrowRightFromLine,
  AlertTriangle,
} from 'lucide-react';
import { ShippingType } from '../../../../../models/Coupang';
import type { CpGpMatchedProduct } from '../../../../../models/Coupang';
import type { ConvertCpOptionsToWpRequest, RestructuredProduct } from '../../../../../infrastructure/SourcingClient';
import { sourcingClient } from '../../../../../infrastructure/SourcingClient';
import { cpGpMatchClient } from '../../../../../infrastructure/CpGpMatchClient';
import { IdBadge, IdBadgeType } from '../../../../../components/ui/badge/IdBadge';
import { StatusBadge } from '../../../../../components/ui/badge/StatusBadge';

interface ProductRestructureSectionProps {
  matchedProduct: CpGpMatchedProduct;
  unifiedProduct: any;
  onRefetch: () => Promise<any>;
}

export const ProductRestructureSection: React.FC<ProductRestructureSectionProps> = ({
  matchedProduct,
  unifiedProduct,
  onRefetch,
}) => {
  // Resizable layout state
  const [cpOptionsWidth, setCpOptionsWidth] = useState(65);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sorting state for CP Options
  const [sortBy, setSortBy] = useState<'itemName' | 'itemId' | 'delivery' | 'price'>('delivery');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reorderedOptions, setReorderedOptions] = useState<any[] | null>(null);
  const [showAllOptions, setShowAllOptions] = useState(false);

  // Checkbox state for CP Options
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());

  // Restructure state
  const [restructureLoading, setRestructureLoading] = useState(false);
  const [restructuredData, setRestructuredData] = useState<any>(null);

  // CP Options refresh state
  const [cpOptionsRefreshing, setCpOptionsRefreshing] = useState(false);
  const [cpOptionsRefreshedAt, setCpOptionsRefreshedAt] = useState<string | null>(null);

  // Save structure state
  const [saveStructureLoading, setSaveStructureLoading] = useState(false);

  // Edit/Delete state for SS products
  const [editedProducts, setEditedProducts] = useState<Map<number, RestructuredProduct>>(new Map());
  const [deletedProductIndices, setDeletedProductIndices] = useState<Set<number>>(new Set());
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);

  // Track failed images
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

  // Load saved SS Product Structure from matched product
  const savedSsStructure = useMemo(() => {
    if (!matchedProduct?.ssRestructuredProducts || matchedProduct.ssRestructuredProducts.length === 0) {
      return null;
    }

    const products = matchedProduct.ssRestructuredProducts;

    return {
      cpProductId: matchedProduct.cpProductId,
      products: products,
      stats: {
        cpOptionsCnt: products.length,
        variable: products.filter((p: RestructuredProduct) => p.type === 'variable').length,
        variation: products.filter((p: RestructuredProduct) => p.type === 'variation').length,
        simple: products.filter((p: RestructuredProduct) => p.type === 'simple').length,
        excluded: 0,
      },
    };
  }, [matchedProduct?.ssRestructuredProducts, matchedProduct?.cpProductId]);

  // Use restructured data if available, otherwise use saved data
  const currentSsStructure = restructuredData || savedSsStructure;

  // Check if there are too many variations (more than 3 per variable)
  const hasExcessiveVariations = useMemo(() => {
    if (!currentSsStructure?.stats) {
      return false;
    }
    const { variable, variation } = currentSsStructure.stats;
    // If there's 1 variable and more than 3 variations, that's excessive
    // Ideal is up to 3 variations per variable
    if (variable > 0 && variation > variable * 3) {
      return true;
    }
    return false;
  }, [currentSsStructure?.stats]);

  // Helper function to get the matching CP option image for an SS product
  const getMatchingCpOptionImage = useCallback(
    (ssProduct: any): string | null => {
      if (!matchedProduct?.cpDetailedProduct?.options || !ssProduct.cpItemId) {
        return matchedProduct?.cpDetailedProduct?.thumbnailImageUrl || null;
      }

      const matchingOption = matchedProduct.cpDetailedProduct.options.find(
        (option) => option.itemId === ssProduct.cpItemId,
      );

      return (
        matchingOption?.hoverImageUrl ||
        matchingOption?.imageUrl ||
        matchedProduct?.cpDetailedProduct?.thumbnailImageUrl ||
        null
      );
    },
    [matchedProduct?.cpDetailedProduct?.options, matchedProduct?.cpDetailedProduct?.thumbnailImageUrl],
  );

  // Handle image error
  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages((prev) => new Set(prev).add(imageUrl));
  }, []);

  // Resizable handlers
  const handleMouseDown = () => {
    setIsResizing(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      const newWidth = Math.min(Math.max((mouseX / containerWidth) * 100, 25), 75);
      setCpOptionsWidth(newWidth);
    },
    [isResizing],
  );

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add/remove event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove]);

  // Reset reordered options when product data changes
  useEffect(() => {
    setReorderedOptions(null);
    setSortBy('delivery');
  }, [matchedProduct?.cpProductId, matchedProduct?.cpItemId]);

  // Initialize checked options based on SS product structure
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (matchedProduct?.cpDetailedProduct?.options && !hasInitializedRef.current) {
      const initialChecked = new Set<string>();

      // Only select options from saved restructure data
      if (savedSsStructure?.products) {
        matchedProduct.cpDetailedProduct.options.forEach((option) => {
          const hasStock = option.stock !== false;
          const hasMatchingSSProduct = savedSsStructure.products.some(
            (product: RestructuredProduct) => product.cpItemId === option.itemId,
          );
          if (hasMatchingSSProduct && hasStock) {
            initialChecked.add(option.itemId || '');
          }
        });
      }
      // If no saved structure, leave empty (no auto-selection)

      setCheckedOptions(initialChecked);
      hasInitializedRef.current = true;
    }
  }, [savedSsStructure, matchedProduct?.cpDetailedProduct?.options]);

  // Reset initialization flag when product changes
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [matchedProduct?.cpProductId, matchedProduct?.cpItemId]);

  // Initialize refreshedAt from matched product
  useEffect(() => {
    if (matchedProduct?.updatedAt && !cpOptionsRefreshedAt) {
      setCpOptionsRefreshedAt(matchedProduct.updatedAt);
    }
  }, [matchedProduct?.updatedAt, cpOptionsRefreshedAt]);

  // Sorting and drag handlers
  const getSortedOptions = (options: any[]) => {
    const baseOptions = reorderedOptions || options;
    const sortedOptions = [...baseOptions];

    switch (sortBy) {
      case 'itemName':
        return sortedOptions.sort((a, b) => a.itemName.localeCompare(b.itemName));
      case 'itemId':
        return sortedOptions.sort((a, b) => (a.itemId || '').localeCompare(b.itemId || ''));
      case 'delivery':
        return sortedOptions.sort((a, b) => {
          const getDeliveryPriority = (shippingType: string) => {
            if (shippingType === ShippingType.ROCKET) {
              return 1;
            }
            if (shippingType === ShippingType.SELLER_ROCKET) {
              return 2;
            }
            if (shippingType === ShippingType.SELLER_DELIVERY) {
              return 3;
            }
            return 4;
          };
          return (
            getDeliveryPriority(a.delivery?.shippingType || '') - getDeliveryPriority(b.delivery?.shippingType || '')
          );
        });
      case 'price':
        return sortedOptions.sort((a, b) => {
          const priceA = parseFloat(a.finalPrice?.replace(/[^\d]/g, '') || '0');
          const priceB = parseFloat(b.finalPrice?.replace(/[^\d]/g, '') || '0');
          return priceA - priceB;
        });
      default:
        return sortedOptions;
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || !unifiedProduct.options) {
      return;
    }

    const currentOptions = reorderedOptions || unifiedProduct.options;
    const newOptions = [...currentOptions];

    const draggedItem = newOptions[draggedIndex];
    newOptions.splice(draggedIndex, 1);
    newOptions.splice(dropIndex, 0, draggedItem);

    setReorderedOptions(newOptions);
    setSortBy('delivery');
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const toggleOptionCheck = (itemId: string) => {
    setCheckedOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleRefreshCpOptions = async () => {
    if (!matchedProduct) {
      return;
    }

    setCpOptionsRefreshing(true);
    try {
      const cpVendorItemId = matchedProduct.cpDetailedProduct.options?.[0]?.vendorItemId || matchedProduct.cpItemId;

      const result = await sourcingClient.refreshCpOptions({
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
        cpVendorItemId,
      });

      if (result.success && result.data?.options) {
        setCpOptionsRefreshedAt(result.data.refreshedAt);

        // Auto-select options after refresh: "x 1개" + rocket + in stock + saved restructure data
        const autoSelected = new Set<string>();

        result.data.options.forEach((option: any) => {
          const is1Pack = /[x×X]\s*1\s*개/.test(option.itemName || '');
          const isRocketOrSellerRocket =
            option.delivery?.shippingType === ShippingType.ROCKET ||
            option.delivery?.shippingType === ShippingType.SELLER_ROCKET;
          const hasStock = option.stock !== false;

          // Select "x 1개" + rocket/seller_rocket + in stock
          if (is1Pack && isRocketOrSellerRocket && hasStock) {
            autoSelected.add(option.itemId || '');
          }

          // Also select options from saved restructure data
          if (savedSsStructure?.products) {
            const hasMatchingSSProduct = savedSsStructure.products.some(
              (product: RestructuredProduct) => product.cpItemId === option.itemId,
            );
            if (hasMatchingSSProduct && hasStock) {
              autoSelected.add(option.itemId || '');
            }
          }
        });

        setCheckedOptions(autoSelected);

        await onRefetch();

        setToastMessage(
          `CP Options refreshed successfully! (${result.data.options.length} options, ${autoSelected.size} selected)`,
        );
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage(`Failed to refresh CP Options: ${result.message || 'Unknown error'}`);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage(`Error refreshing CP Options: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setCpOptionsRefreshing(false);
    }
  };

  const handleRestructure = async () => {
    if (!matchedProduct || checkedOptions.size === 0) {
      return;
    }

    setRestructureLoading(true);

    try {
      const coupangProduct = matchedProduct.cpDetailedProduct;
      const glowpickProduct = matchedProduct.gpDetailedProduct;
      const scAgentEngData = glowpickProduct?.scAgentEngData;

      const selectedOptions = coupangProduct.options?.filter((option) => checkedOptions.has(option.itemId || '')) || [];

      const requestData: ConvertCpOptionsToWpRequest = {
        brand: scAgentEngData?.brand || '',
        ssNameOnly: scAgentEngData?.name || '',
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
        cpOptions: selectedOptions,
      };

      if (requestData.brand === '' || requestData.ssNameOnly === '') {
        setToastMessage('Brand and Name are required');
        setToastVariant('danger');
        setShowToast(true);
        return;
      }
      const result = await sourcingClient.convertCpOptionsToWp(requestData);

      if (result.success && result.data) {
        setRestructuredData({
          ...result.data,
          products: result.data.products,
        });
        setEditedProducts(new Map());
        setDeletedProductIndices(new Set());
        // Show success toast
        setToastMessage(`Restructure completed successfully! (${result.data.products.length} products)`);
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage(`Restructure failed: ${result.message || 'Unknown error'}`);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage(`Restructure error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setRestructureLoading(false);
    }
  };

  const handleSaveStructure = async () => {
    if (!matchedProduct || !currentSsStructure) {
      return;
    }

    const finalProductsCount = currentSsStructure.products.filter(
      (_: RestructuredProduct, index: number) => !deletedProductIndices.has(index),
    ).length;

    const confirmMessage =
      editedProducts.size > 0 || deletedProductIndices.size > 0
        ? `Save changes to ${finalProductsCount} products?\n\n${editedProducts.size} edited, ${deletedProductIndices.size} deleted`
        : `Save current structure with ${finalProductsCount} products?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    setSaveStructureLoading(true);

    try {
      const finalProducts = currentSsStructure.products
        .filter((_: RestructuredProduct, index: number) => !deletedProductIndices.has(index))
        .map((product: RestructuredProduct, index: number) => {
          const productToSave = editedProducts.has(index) ? editedProducts.get(index)! : product;

          return {
            ...productToSave,
            attributeSize: productToSave.attributeSize || '',
            attributePack: productToSave.attributePack || '',
            attributeColor: productToSave.attributeColor || '',
          };
        });

      const result = await cpGpMatchClient.updateMatch(matchedProduct.cpProductId, matchedProduct.cpItemId, {
        ssRestructuredProducts: finalProducts,
      });

      if (result.success) {
        const savedItemIds = finalProducts.map((p: RestructuredProduct) => p.cpItemId).filter(Boolean);
        setCheckedOptions(new Set(savedItemIds));
        setEditedProducts(new Map());
        setDeletedProductIndices(new Set());
        setRestructuredData(null);
        await onRefetch();
      } else {
        alert(`Failed to save structure: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Error saving structure: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaveStructureLoading(false);
    }
  };

  const handleEditProduct = (index: number) => {
    setEditingProductIndex(index);
  };

  const handleDeleteProduct = (index: number) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setDeletedProductIndices((prev) => new Set(prev).add(index));
    }
  };

  const handleSaveEdit = () => {
    setEditingProductIndex(null);
  };

  const handleCancelEdit = (index: number) => {
    // Remove any edits for this product
    setEditedProducts((prev) => {
      const newMap = new Map(prev);
      newMap.delete(index);
      return newMap;
    });
    setEditingProductIndex(null);
  };

  const handleEditChange = (index: number, field: keyof RestructuredProduct, value: string) => {
    setEditedProducts((prev) => {
      const newMap = new Map(prev);
      const currentProduct = newMap.get(index) || currentSsStructure?.products[index];
      if (currentProduct) {
        newMap.set(index, {
          ...currentProduct,
          [field]: value,
        });
      }
      return newMap;
    });
  };

  const hasModifications = editedProducts.size > 0 || deletedProductIndices.size > 0;

  // Get pack options from parent variable product for variations
  const getPackOptionsFromParent = useCallback(() => {
    if (!currentSsStructure?.products) {
      return [];
    }

    const parentProduct = currentSsStructure.products.find((p: RestructuredProduct) => p.type === 'variable');
    if (!parentProduct) {
      return [];
    }

    const parentPack = parentProduct.attributePack || '';
    let packOptions: string[] = [];

    if (parentPack) {
      if (parentPack.includes('|')) {
        packOptions = parentPack
          .split('|')
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0);
      } else if (parentPack.includes(',')) {
        packOptions = parentPack
          .split(',')
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0);
      } else if (parentPack.includes(';')) {
        packOptions = parentPack
          .split(';')
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0);
      } else {
        packOptions = [parentPack.trim()].filter((p: string) => p.length > 0);
      }
    }

    return packOptions;
  }, [currentSsStructure?.products]);

  return (
    <>
      {/* Product Restructure Section */}
      <Card className="mb-0">
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">Product Restructure</h5>
              <small className="text-muted">Transform Coupang product options into SkinSeoul product structure</small>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRefreshCpOptions}
                disabled={cpOptionsRefreshing}
                style={{ fontSize: '0.9rem' }}
                title="Refresh CP Options - Get latest options from Coupang"
              >
                {cpOptionsRefreshing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} className="me-1" />
                    Refresh CP Options
                  </>
                )}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRestructure}
                disabled={restructureLoading || checkedOptions.size === 0}
                style={{ fontSize: '0.9rem' }}
                title={
                  checkedOptions.size === 0
                    ? 'Select at least one CP option'
                    : 'Restructure - Preview and edit results before saving'
                }
              >
                {restructureLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Restructuring...
                  </>
                ) : (
                  <>
                    <ArrowRightFromLine size={14} className="me-1" />
                    <span>Restructure ({checkedOptions.size} selected)</span>
                  </>
                )}
              </Button>
              <Button
                variant={hasModifications ? 'warning' : 'success'}
                size="sm"
                onClick={handleSaveStructure}
                disabled={saveStructureLoading || !currentSsStructure}
                style={{ fontSize: '0.9rem' }}
                title={
                  !currentSsStructure
                    ? 'No structure available. Run Restructure first.'
                    : 'Save the current structure to database'
                }
              >
                {saveStructureLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Saving...
                  </>
                ) : hasModifications ? (
                  `Save Changes (${editedProducts.size} edited, ${deletedProductIndices.size} deleted)`
                ) : (
                  'Save Current Structure'
                )}
              </Button>
            </div>
          </div>
        </Card.Header>
      </Card>

      {/* CP Options & SS Product Structure */}
      <div ref={containerRef} className="d-flex mb-4" style={{ gap: '0px' }}>
        {/* CP Options - Left Side */}
        <div style={{ width: `${cpOptionsWidth}%`, minWidth: '300px' }}>
          <Card className="h-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <h6 className="mb-0">
                    CP Options{' '}
                    {unifiedProduct.options && unifiedProduct.options.length > 0
                      ? `(${checkedOptions.size}/${unifiedProduct.options.length} selected)`
                      : '(No options available)'}
                  </h6>
                  {unifiedProduct.options && unifiedProduct.options.length > 0 && (
                    <>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" style={{ fontSize: '0.7rem' }}>
                          Sort:{' '}
                          {sortBy === 'delivery'
                            ? 'Delivery (Rocket First)'
                            : sortBy === 'itemName'
                              ? 'Item Name'
                              : sortBy === 'itemId'
                                ? 'Item ID'
                                : 'Price'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy('delivery');
                              setReorderedOptions(null);
                            }}
                          >
                            Delivery (Rocket First)
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy('itemName');
                              setReorderedOptions(null);
                            }}
                          >
                            Item Name
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy('itemId');
                              setReorderedOptions(null);
                            }}
                          >
                            Item ID
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy('price');
                              setReorderedOptions(null);
                            }}
                          >
                            Price (Low to High)
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>

                      {/* Selection Controls */}
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-success"
                          size="sm"
                          style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                          onClick={() => {
                            const allItemIds = new Set<string>(
                              unifiedProduct.options?.map((opt: any) => opt.itemId || '').filter(Boolean) || [],
                            );
                            setCheckedOptions(allItemIds);
                          }}
                          title="Select all options"
                        >
                          All
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                          onClick={() => setCheckedOptions(new Set())}
                          title="Clear all selections"
                        >
                          None
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {cpOptionsRefreshedAt && (
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                      <RefreshCw size={12} className="me-1" />
                      Refreshed on{' '}
                      {new Date(cpOptionsRefreshedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </small>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleRefreshCpOptions}
                    disabled={cpOptionsRefreshing}
                    style={{ fontSize: '0.7rem' }}
                    title="Refresh CP Options - Get latest options from Coupang"
                  >
                    {cpOptionsRefreshing ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} className="me-1" />
                        Refresh
                      </>
                    )}
                  </Button>

                  <IdBadge type={IdBadgeType.CPPI} value={matchedProduct.cpProductId} size="sm" />
                </div>
              </div>
            </Card.Header>

            {/* Instructional Text for Drag & Drop */}
            {unifiedProduct.options && unifiedProduct.options.length > 0 && (
              <div className="px-3 py-2 bg-light border-bottom">
                <small className="text-muted d-flex align-items-center gap-2">
                  <span className="fw-bold">💡 Tip:</span>
                  <span>
                    Check/uncheck options to filter SS Product Structure. Drag and drop to reorder, or use sort options
                    for different arrangements.
                  </span>
                </small>
              </div>
            )}

            <Card.Body style={{ position: 'relative', minHeight: '200px' }}>
              {/* Loading Overlay */}
              {cpOptionsRefreshing && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                  }}
                >
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>
                    Refreshing CP options...
                  </p>
                </div>
              )}
              {unifiedProduct.options && unifiedProduct.options.length > 0 ? (
                <>
                  <Row className="g-3">
                    {(() => {
                      const sortedOptions = getSortedOptions(unifiedProduct.options);
                      const displayedOptions = showAllOptions ? sortedOptions : sortedOptions.slice(0, 12);

                      return displayedOptions.map((option, index) => (
                        <Col key={`${option.itemId}-${index}`} md={6}>
                          <div
                            className={`border rounded p-2 h-100 d-flex ${
                              draggedIndex === index
                                ? 'opacity-75 shadow-lg border-warning bg-warning bg-opacity-10'
                                : ''
                            } ${
                              dragOverIndex === index
                                ? 'border-primary border-3 bg-primary bg-opacity-10 shadow-sm'
                                : 'border-2'
                            } ${
                              draggedIndex !== null && draggedIndex !== index
                                ? 'border-dashed border-secondary bg-light bg-opacity-50'
                                : ''
                            }`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => handleDrop(e, index)}
                            style={{
                              cursor: draggedIndex === index ? 'grabbing' : 'grab',
                              transition: 'all 0.3s ease',
                              transform: draggedIndex === index ? 'rotate(2deg) scale(1.02)' : 'none',
                              zIndex: draggedIndex === index ? 1000 : 1,
                            }}
                          >
                            {/* Interactive Checkbox */}
                            <div className="me-2 flex-shrink-0 d-flex align-items-start">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (option.stock !== false) {
                                    toggleOptionCheck(option.itemId || '');
                                  }
                                }}
                                style={{
                                  cursor: option.stock === false ? 'not-allowed' : 'pointer',
                                  padding: '2px',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                  opacity: option.stock === false ? 0.5 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  if (option.stock !== false) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                title={
                                  option.stock === false
                                    ? 'Out of stock'
                                    : checkedOptions.has(option.itemId || '')
                                      ? 'Click to uncheck'
                                      : 'Click to check'
                                }
                              >
                                <div
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    border: checkedOptions.has(option.itemId || '')
                                      ? '2px solid #28a745'
                                      : '2px solid #dee2e6',
                                    borderRadius: '4px',
                                    backgroundColor: checkedOptions.has(option.itemId || '')
                                      ? '#28a745'
                                      : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  {checkedOptions.has(option.itemId || '') && (
                                    <Check size={12} className="text-white" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Left side: Product info */}
                            <div className="flex-grow-1">
                              {/* Option Name/Size with link */}
                              <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                                <a
                                  href={`https://www.coupang.com/vp/products/${matchedProduct.cpProductId}?itemId=${option.itemId}&vendorItemId=${option.vendorItemId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none d-flex align-items-center gap-1"
                                  style={{ color: 'inherit' }}
                                >
                                  {option.itemName}
                                  <ExternalLink size={12} />
                                </a>
                              </div>

                              {/* Price with Unit Price */}
                              <div className="d-flex align-items-center mb-1">
                                <span className="text-danger fw-bold me-2" style={{ fontSize: '0.9rem' }}>
                                  {option.finalPrice}
                                </span>
                                {option.unitPrice && (
                                  <span className="text-muted me-2" style={{ fontSize: '0.8rem' }}>
                                    {option.unitPrice}
                                  </span>
                                )}
                                {!option.stock && (
                                  <span className="text-danger ms-1" style={{ fontSize: '0.8rem' }}>
                                    • 품절
                                  </span>
                                )}
                              </div>

                              {/* Delivery Information */}
                              <div className="d-flex align-items-center mb-1" style={{ fontSize: '0.8rem' }}>
                                {option.delivery?.deliveryDescription && (
                                  <span className="text-success me-1" style={{ fontSize: '0.75rem' }}>
                                    {option.delivery.deliveryDescription.length > 30
                                      ? `${option.delivery.deliveryDescription.substring(0, 30)}...`
                                      : option.delivery.deliveryDescription}
                                  </span>
                                )}
                                {option.delivery?.shippingType === ShippingType.ROCKET && (
                                  <div className="d-flex align-items-center">
                                    <span className="text-success me-1">✓</span>
                                    <img
                                      src="/assets/rocket-logo.png"
                                      alt="로켓배송"
                                      style={{ height: '16px', width: 'auto' }}
                                      className="me-1"
                                    />
                                  </div>
                                )}
                                {option.delivery?.shippingType === ShippingType.SELLER_ROCKET && (
                                  <div className="d-flex align-items-center">
                                    <img
                                      src="/assets/seller-rocket-logo.png"
                                      alt="판매자로켓"
                                      style={{ height: '16px', width: 'auto' }}
                                      className="me-1"
                                    />
                                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      판매자로켓
                                    </span>
                                  </div>
                                )}
                                {option.delivery?.shippingType === ShippingType.SELLER_DELIVERY && (
                                  <span className="text-success" style={{ fontSize: '0.7rem' }}>
                                    무료배송
                                  </span>
                                )}
                              </div>

                              {/* ID Badges - Bottom Row */}
                              <div className="d-flex align-items-center gap-2">
                                <IdBadge type={IdBadgeType.CPII} value={option.itemId || ''} size="sm" />
                                <IdBadge type={IdBadgeType.CPVI} value={option.vendorItemId || ''} size="sm" />
                              </div>
                            </div>

                            {/* Right side: Option image */}
                            {(option.hoverImageUrl || option.imageUrl) && (
                              <div className="ms-2 flex-shrink-0">
                                <div style={{ width: '80px' }}>
                                  <img
                                    src={option.hoverImageUrl || option.imageUrl}
                                    alt={option.itemName}
                                    className="rounded border"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                  />
                                  <div
                                    style={{
                                      fontSize: '0.6rem',
                                      color: '#666',
                                      marginTop: '4px',
                                      wordBreak: 'break-all',
                                      textAlign: 'center',
                                    }}
                                    title={option.hoverImageUrl || option.imageUrl}
                                  >
                                    {(option.hoverImageUrl || option.imageUrl)?.split('/').pop()}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Drag handle */}
                            <div className="ms-2 flex-shrink-0 d-flex align-items-center" title="Drag to reorder">
                              <GripVertical size={16} className="text-muted" style={{ cursor: 'grab' }} />
                            </div>
                          </div>
                        </Col>
                      ));
                    })()}
                  </Row>

                  {unifiedProduct.options && unifiedProduct.options.length > 12 && (
                    <div className="text-center mt-3">
                      <Button variant="outline-primary" size="sm" onClick={() => setShowAllOptions(!showAllOptions)}>
                        {showAllOptions
                          ? 'Show Less'
                          : `Show More (${unifiedProduct.options.length - 12} more options)`}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <Package size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">No Options Available</h6>
                  <p className="text-muted small mb-0">This product does not have any options data to display.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Resizable Divider */}
        <div
          onMouseDown={handleMouseDown}
          style={{
            width: '8px',
            backgroundColor: '#dee2e6',
            cursor: 'col-resize',
            position: 'relative',
            flexShrink: 0,
            borderLeft: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '2px',
              height: '40px',
              backgroundColor: '#6c757d',
              borderRadius: '1px',
            }}
          />
        </div>

        {/* SS Product Structure - Right Side */}
        <div style={{ width: `${100 - cpOptionsWidth}%`, minWidth: '250px' }}>
          <Card className="h-100" style={{ position: 'relative' }}>
            {/* Loading Overlay */}
            {restructureLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  backdropFilter: 'blur(2px)',
                }}
              >
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>
                  Restructuring products...
                </p>
              </div>
            )}
            <Card.Header>
              <h6 className="mb-0">
                SS Product Structure Result
                {currentSsStructure?.products && ` (${currentSsStructure.products.length})`}
              </h6>
            </Card.Header>
            <Card.Body>
              {currentSsStructure ? (
                <>
                  {/* Conversion Stats */}
                  <div className="mb-3">
                    <div className="d-flex gap-2 flex-wrap justify-content-center align-items-center">
                      <StatusBadge type="primary" label={`Variable: ${currentSsStructure.stats.variable}`} size="md" />
                      <StatusBadge
                        type="warning"
                        label={`Variation: ${currentSsStructure.stats.variation}`}
                        size="md"
                      />
                      <StatusBadge type="success" label={`Simple: ${currentSsStructure.stats.simple}`} size="md" />
                      {hasExcessiveVariations && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="excessive-variations-tooltip">
                              <strong>Too Many Variations!</strong>
                              <br />
                              Recommended: Maximum 3 variations per variable product.
                              <br />
                              Current: {currentSsStructure.stats.variation} variations for{' '}
                              {currentSsStructure.stats.variable} variable product(s).
                              <br />
                              Please delete excess variations to maintain optimal product structure.
                            </Tooltip>
                          }
                        >
                          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
                            <AlertTriangle size={20} className="text-danger" />
                            <span className="text-danger" style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                              Warning
                            </span>
                          </div>
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>

                  {/* SS Products List */}
                  <div className="p-1">
                    {currentSsStructure.products?.map((product: RestructuredProduct, index: number) => {
                      const isDeleted = deletedProductIndices.has(index);
                      const isEdited = editedProducts.has(index);
                      const displayProduct = isEdited ? editedProducts.get(index)! : product;
                      const scAgentEngData = matchedProduct?.gpDetailedProduct?.scAgentEngData;

                      // Find variable product to inherit attributes
                      const variableProduct = currentSsStructure.products?.find(
                        (p: RestructuredProduct) => p.type === 'variable',
                      );
                      const inheritedSize =
                        displayProduct.type === 'variation' && !displayProduct.attributeSize
                          ? variableProduct?.attributeSize
                          : displayProduct.attributeSize;
                      const inheritedColor =
                        displayProduct.type === 'variation' && !displayProduct.attributeColor
                          ? variableProduct?.attributeColor
                          : displayProduct.attributeColor;

                      if (isDeleted) {
                        return (
                          <Card
                            key={index}
                            className="mb-2 shadow-sm border-danger"
                            style={{ overflow: 'hidden', opacity: 0.5 }}
                          >
                            <Card.Body className="p-2">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <Trash2 size={16} className="text-danger me-2" />
                                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Marked for deletion: {product.name}
                                  </span>
                                </div>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    setDeletedProductIndices((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(index);
                                      return newSet;
                                    })
                                  }
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  Undo
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      }

                      const isEditing = editingProductIndex === index;

                      return (
                        <Card
                          key={index}
                          className={`mb-2 shadow-sm ${isEdited ? 'border-warning' : ''} ${isEditing ? 'border-primary' : ''}`}
                          style={{ overflow: 'hidden' }}
                        >
                          <Card.Body className="p-2">
                            <div className="d-flex">
                              {/* Number */}
                              <div className="me-2 flex-shrink-0 d-flex align-items-start">
                                <span
                                  style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '2px', color: '#6c757d' }}
                                >
                                  {index + 1}
                                </span>
                              </div>

                              {/* Product Image */}
                              <div className="me-3 flex-shrink-0">
                                {(() => {
                                  const imageUrl =
                                    displayProduct.optionImageUrl || getMatchingCpOptionImage(displayProduct);
                                  const hasFailed = imageUrl && failedImages.has(imageUrl);
                                  const filename = imageUrl ? imageUrl.split('/').pop() || imageUrl : 'No image';

                                  if (!imageUrl || hasFailed) {
                                    return (
                                      <div style={{ width: '200px' }}>
                                        <div
                                          style={{
                                            width: '200px',
                                            height: '200px',
                                            borderRadius: '4px',
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Package size={64} color="#aaa" />
                                        </div>
                                        <div
                                          style={{
                                            fontSize: '0.65rem',
                                            color: '#999',
                                            marginTop: '4px',
                                            wordBreak: 'break-all',
                                            textAlign: 'center',
                                          }}
                                        >
                                          {filename}
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div style={{ width: '200px' }}>
                                      <img
                                        src={imageUrl}
                                        alt={displayProduct.cpNameKr}
                                        style={{
                                          width: '200px',
                                          height: '200px',
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                          border: '1px solid #dee2e6',
                                        }}
                                        onError={() => handleImageError(imageUrl)}
                                      />
                                      <div
                                        style={{
                                          fontSize: '0.65rem',
                                          color: '#666',
                                          marginTop: '4px',
                                          wordBreak: 'break-all',
                                          textAlign: 'center',
                                        }}
                                        title={imageUrl}
                                      >
                                        {filename}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Product Details */}
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                {/* Header with badges and edit status */}
                                <div className="d-flex gap-2 flex-wrap mb-2 align-items-center">
                                  <StatusBadge
                                    type="productType"
                                    value={displayProduct.type}
                                    label={displayProduct.type.charAt(0).toUpperCase() + displayProduct.type.slice(1)}
                                    size="sm"
                                  />
                                  {isEdited && (
                                    <Badge bg="warning" style={{ fontSize: '0.65rem' }}>
                                      <Edit2 size={10} className="me-1" />
                                      Edited
                                    </Badge>
                                  )}
                                  {isEditing && <StatusBadge type="primary" label="Editing..." size="sm" />}
                                </div>

                                {isEditing ? (
                                  <>
                                    {/* Edit Mode */}
                                    <Form.Group className="mb-2">
                                      <Form.Label style={{ fontSize: '0.7rem', marginBottom: '2px' }}>
                                        Name (Inherited from GP Eng Name)
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        value={[
                                          scAgentEngData?.name || '',
                                          displayProduct?.attributeSize || '',
                                          displayProduct?.attributeColor || '',
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                        readOnly
                                        style={{ fontSize: '0.75rem', backgroundColor: '#f8f9fa', color: '#28a745' }}
                                      />
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                      <Form.Label style={{ fontSize: '0.7rem', marginBottom: '2px' }}>
                                        cpNameKr (GP Kr Name + Size or Color)
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        value={
                                          [
                                            matchedProduct?.gpDetailedProduct?.name || '',
                                            displayProduct?.attributeSize || '',
                                            displayProduct?.attributeColor || '',
                                          ]
                                            .filter(Boolean)
                                            .join(' ') || ''
                                        }
                                        readOnly
                                        style={{ fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}
                                      />
                                    </Form.Group>

                                    {/* Show Size for variable and simple */}
                                    {(displayProduct.type === 'variable' || displayProduct.type === 'simple') && (
                                      <Form.Group className="mb-2">
                                        <Form.Label style={{ fontSize: '0.7rem', marginBottom: '2px' }}>
                                          Size
                                        </Form.Label>
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          value={displayProduct.attributeSize || ''}
                                          onChange={(e) => handleEditChange(index, 'attributeSize', e.target.value)}
                                          style={{ fontSize: '0.75rem' }}
                                        />
                                      </Form.Group>
                                    )}

                                    {/* Show Pack for all types */}
                                    <Form.Group className="mb-2">
                                      <Form.Label style={{ fontSize: '0.7rem', marginBottom: '2px' }}>Pack</Form.Label>
                                      {displayProduct.type === 'variation' ? (
                                        (() => {
                                          const packOptions = getPackOptionsFromParent();
                                          if (packOptions.length > 0) {
                                            return (
                                              <Form.Select
                                                size="sm"
                                                value={displayProduct.attributePack || ''}
                                                onChange={(e) =>
                                                  handleEditChange(index, 'attributePack', e.target.value)
                                                }
                                                style={{ fontSize: '0.75rem' }}
                                              >
                                                <option value="">Select Pack</option>
                                                {packOptions.map((option) => (
                                                  <option key={option} value={option}>
                                                    {option}
                                                  </option>
                                                ))}
                                              </Form.Select>
                                            );
                                          }
                                          return (
                                            <Form.Control
                                              type="text"
                                              size="sm"
                                              value={displayProduct.attributePack || ''}
                                              onChange={(e) => handleEditChange(index, 'attributePack', e.target.value)}
                                              style={{ fontSize: '0.75rem' }}
                                              placeholder="No parent pack options available"
                                            />
                                          );
                                        })()
                                      ) : (
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          value={displayProduct.attributePack || ''}
                                          onChange={(e) => handleEditChange(index, 'attributePack', e.target.value)}
                                          style={{ fontSize: '0.75rem' }}
                                        />
                                      )}
                                    </Form.Group>

                                    {/* Show Color for variable and simple */}
                                    {(displayProduct.type === 'variable' || displayProduct.type === 'simple') && (
                                      <Form.Group className="mb-2">
                                        <Form.Label style={{ fontSize: '0.7rem', marginBottom: '2px' }}>
                                          Color
                                        </Form.Label>
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          value={displayProduct.attributeColor || ''}
                                          onChange={(e) => handleEditChange(index, 'attributeColor', e.target.value)}
                                          style={{ fontSize: '0.75rem' }}
                                        />
                                      </Form.Group>
                                    )}

                                    <div className="d-flex gap-2 mt-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleSaveEdit()}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        <Check size={12} className="me-1" />
                                        Save
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleCancelEdit(index)}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {/* View Mode */}
                                    {/* English Product Name (without brand) */}
                                    {displayProduct.name && (
                                      <div className="mb-1" style={{ fontSize: '0.8rem', color: '#28a745' }}>
                                        name:{' '}
                                        {[
                                          scAgentEngData?.name || '',
                                          displayProduct?.attributeSize || '',
                                          displayProduct?.attributeColor || '',
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                      </div>
                                    )}

                                    {/* Korean Product Name */}
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                      cpNameKr:{' '}
                                      {[
                                        matchedProduct?.gpDetailedProduct?.name || '',
                                        displayProduct?.attributeSize || '',
                                        displayProduct?.attributeColor || '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    </div>

                                    {/* Attributes */}
                                    {(inheritedSize || displayProduct.attributePack || inheritedColor) && (
                                      <div className="mb-2" style={{ fontSize: '0.75rem' }}>
                                        {inheritedSize && (
                                          <StatusBadge
                                            type="secondary"
                                            label={`Size: ${inheritedSize}`}
                                            size="sm"
                                            className="me-1"
                                          />
                                        )}
                                        {displayProduct.attributePack && (
                                          <StatusBadge
                                            type="secondary"
                                            label={`Pack: ${displayProduct.attributePack}`}
                                            size="sm"
                                            className="me-1"
                                          />
                                        )}
                                        {inheritedColor && (
                                          <StatusBadge
                                            type="secondary"
                                            label={`Color: ${inheritedColor}`}
                                            size="sm"
                                            className="me-1"
                                          />
                                        )}
                                      </div>
                                    )}

                                    {/* CP Item Name */}
                                    {displayProduct.cpItemName && (
                                      <div className="mb-1" style={{ fontSize: '0.7rem' }}>
                                        <span className="text-muted">Source CP Option: </span>
                                        <span className="text-dark">{displayProduct.cpItemName}</span>
                                      </div>
                                    )}

                                    {/* Prices */}
                                    <div className="d-flex gap-3 mb-2">
                                      {displayProduct.regularPrice && (
                                        <div>
                                          <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                            Regular Price
                                          </small>
                                          <div className="text-success fw-bold" style={{ fontSize: '0.85rem' }}>
                                            SGD ${displayProduct.regularPrice}
                                          </div>
                                        </div>
                                      )}
                                      {displayProduct.cpListPrice && (
                                        <div>
                                          <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                            CP Price
                                          </small>
                                          <div className="text-primary fw-bold" style={{ fontSize: '0.85rem' }}>
                                            {displayProduct.cpListPrice}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* IDs Row */}
                                    <div className="d-flex gap-2 flex-wrap mb-2">
                                      {displayProduct.cpItemId && (
                                        <IdBadge type={IdBadgeType.CPII} value={displayProduct.cpItemId} size="sm" />
                                      )}
                                      {displayProduct.cpVendorItemId && (
                                        <IdBadge
                                          type={IdBadgeType.CPVI}
                                          value={displayProduct.cpVendorItemId}
                                          size="sm"
                                        />
                                      )}
                                    </div>

                                    {/* URL Slug and Link - Don't show for variations */}
                                    {displayProduct.type !== 'variation' && (
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted small text-truncate" style={{ fontSize: '0.65rem' }}>
                                          {displayProduct.urlSlug}
                                        </div>
                                        <div className="d-flex gap-1 align-items-center">
                                          {displayProduct.cpUrl && (
                                            <a
                                              href={displayProduct.cpUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-decoration-none"
                                              style={{ fontSize: '0.7rem' }}
                                              title="View on Coupang"
                                            >
                                              <ExternalLink size={12} />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* Action Buttons */}
                              {!isEditing && (
                                <div className="ms-2 flex-shrink-0 d-flex flex-column gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditProduct(index)}
                                    style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                                    title="Edit product details"
                                  >
                                    <Edit2 size={12} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteProduct(index)}
                                    style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                                    title="Delete this product"
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <Package size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">No Product Structure Available</h6>
                  <p className="text-muted small mb-3">
                    Click <strong>&quot;Restructure&quot;</strong> button above to generate the SS product structure
                    from selected CP options.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '80px' }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={4000}
          autohide
          bg={toastVariant}
          style={{ minWidth: '400px', fontSize: '1rem' }}
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto" style={{ fontSize: '1.1rem' }}>
              {toastVariant === 'success' ? '✅ Success' : '❌ Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white" style={{ fontSize: '0.95rem' }}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};
